import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiArrowRight, FiCheckCircle, FiXCircle } from 'react-icons/fi';

import Modal from '../common/Modal';

/**
 * Pre-transfer match dialog, shared by Payroll (Danløn employees) and
 * Invoicing (e-conomic customers).
 *
 * The backend sync-preview classifies every Tempus record:
 *   linked   — already paired; nothing to do (shown as a summary count)
 *   update   — paired, and Tempus will be UPDATED FROM Danløn (Danløn is master)
 *   link     — an unambiguous match was found; will be paired on apply
 *   create   — no match; will be created in the external system
 *   decision — ambiguous/conflicting; the admin must pair manually here
 *
 * onConfirm receives the decision list for the sync-apply endpoint.
 */

const Intro = styled.p`color: var(--ve-ink-600); font-size: 0.85rem; margin: 0 0 1rem;`;
const Summary = styled.div`display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1rem;`;
const Pill = styled.span`
  font-size: 0.75rem; font-weight: 600; padding: 0.15rem 0.6rem; border-radius: 999px;
  color: ${({ $tone }) => ($tone === 'ok' ? '#1e8449' : $tone === 'warn' ? '#9a7d0a' : $tone === 'bad' ? 'var(--act-danger)' : 'var(--ve-ink-600)')};
  background: ${({ $tone }) => ($tone === 'ok' ? '#eafaf1' : $tone === 'warn' ? '#fef9e7' : $tone === 'bad' ? '#fdedec' : 'var(--surface-subtle)')};
`;
const RowCard = styled.div`
  border: 1px solid ${({ $needs }) => ($needs ? 'var(--status-warning)' : 'var(--border-subtle)')};
  background: ${({ $needs }) => ($needs ? '#fffdf3' : '#fff')};
  border-radius: 0.4rem; padding: 0.75rem 0.9rem; margin-bottom: 0.65rem;
`;
const RowHead = styled.div`display: flex; justify-content: space-between; gap: 0.75rem; align-items: baseline; flex-wrap: wrap;`;
const RecordName = styled.span`font-weight: 600; color: var(--ve-ink-800); font-size: 0.92rem;`;
const RecordMeta = styled.span`color: var(--ve-ink-500); font-size: 0.78rem; word-break: break-all;`;
const StatusLine = styled.div`
  display: flex; align-items: flex-start; gap: 0.4rem; font-size: 0.82rem; margin-top: 0.4rem;
  color: ${({ $tone }) => ($tone === 'ok' ? '#1e8449' : $tone === 'warn' ? '#9a7d0a' : 'var(--ve-ink-600)')};
  svg { flex-shrink: 0; margin-top: 0.12rem; }
`;
const UpdateList = styled.ul`
  margin: 0.35rem 0 0; padding-left: 1.4rem; font-size: 0.8rem; color: var(--ve-ink-600);
  li { margin: 0.1rem 0; } code { background: var(--surface-subtle); border-radius: 0.2rem; padding: 0 0.25rem; }
`;
const Controls = styled.div`display: flex; gap: 0.6rem; margin-top: 0.6rem; flex-wrap: wrap; align-items: center;`;
const Select = styled.select`
  padding: 0.4rem 0.5rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.85rem;
  max-width: 100%; background: #fff;
  &:focus { outline: none; border-color: var(--act-primary); }
`;
const SectionTitle = styled.h3`font-size: 0.9rem; color: var(--ve-ink-800); margin: 1.25rem 0 0.5rem;`;
const ExternalItem = styled.div`font-size: 0.8rem; color: var(--ve-ink-600); padding: 0.15rem 0; word-break: break-all;`;
const Actions = styled.div`display: flex; gap: 0.75rem; margin-top: 1.25rem; flex-wrap: wrap; align-items: center;`;
const Btn = styled.button`
  padding: 0.55rem 1.1rem; border-radius: 0.25rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; border: none;
  color: #fff; background: ${({ $variant }) => ($variant === 'ghost' ? 'var(--ve-ink-600)' : 'var(--act-primary)')};
  &:hover { filter: brightness(0.94); } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Warn = styled.div`
  display: flex; gap: 0.4rem; align-items: flex-start; font-size: 0.82rem; color: #9a7d0a;
  background: #fef9e7; border: 1px solid var(--status-warning); border-radius: 0.4rem; padding: 0.6rem 0.8rem; margin-bottom: 1rem;
  svg { flex-shrink: 0; margin-top: 0.12rem; }
`;
const ErrorLine = styled.div`display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: var(--act-danger);`;

// Provider adapters: how to read ids/labels from the preview rows.
const ADAPTERS = {
  danlon: {
    rowKey: (row) => row.employeeId,
    externalId: (ext) => String(ext.id),
    externalLabel: (ext) =>
      [ext.name, ext.email, ext.cpr ? `CPR ${ext.cpr}` : ext.domainId ? `ID ${ext.domainId}` : null]
        .filter(Boolean)
        .join(' · '),
    matchedExternal: (row) => row.danlon,
    decision: (row, action, targetId) => ({
      employeeId: row.employeeId,
      action,
      ...(targetId ? { danlonId: targetId } : {}),
    }),
  },
  economic: {
    rowKey: (row) => row.customerId,
    externalId: (ext) => String(ext.customerNumber),
    externalLabel: (ext) =>
      [`#${ext.customerNumber}`, ext.name, ext.cvr ? `CVR ${ext.cvr}` : null, ext.email]
        .filter(Boolean)
        .join(' · '),
    matchedExternal: (row) => row.economic,
    decision: (row, action, targetId) => ({
      customerId: row.customerId,
      action,
      ...(targetId ? { customerNumber: Number(targetId) } : {}),
    }),
  },
};

const SyncMatchDialog = ({ provider, preview, busy, error, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const adapter = ADAPTERS[provider];

  const rows = useMemo(() => (preview.rows || []).filter((r) => r.status !== 'linked'), [preview.rows]);
  const linkedCount = (preview.rows || []).length - rows.length;
  const unmatchedExternal = provider === 'danlon' ? preview.unmatchedDanlon || [] : preview.unmatchedEconomic || [];
  const canCreate = Boolean(preview.canCreate);

  // Per-row choice: { action: 'link'|'create'|'skip'|'', targetId } — seeded
  // from the automatic classification; 'decision' rows start empty so the
  // admin must actively choose.
  const [choices, setChoices] = useState(() => {
    const initial = {};
    rows.forEach((row) => {
      const matched = adapter.matchedExternal(row);
      if ((row.status === 'update' || row.status === 'link') && matched) {
        initial[adapter.rowKey(row)] = { action: 'link', targetId: adapter.externalId(matched) };
      } else if (row.status === 'create') {
        initial[adapter.rowKey(row)] = canCreate ? { action: 'create', targetId: '' } : { action: '', targetId: '' };
      } else {
        initial[adapter.rowKey(row)] = { action: '', targetId: '' };
      }
    });
    return initial;
  });

  const setChoice = (key, patch) => {
    setChoices((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  // Link targets offered for a row: its own candidates first, then every
  // still-unmatched external record.
  const linkTargets = (row) => {
    const seen = new Set();
    const out = [];
    [...(row.candidates || []), ...(adapter.matchedExternal(row) ? [adapter.matchedExternal(row)] : []), ...unmatchedExternal]
      .filter(Boolean)
      .forEach((ext) => {
        const id = adapter.externalId(ext);
        if (!seen.has(id)) {
          seen.add(id);
          out.push(ext);
        }
      });
    return out;
  };

  const incomplete = rows.some((row) => {
    const choice = choices[adapter.rowKey(row)] || {};
    if (!choice.action) return true;
    if (choice.action === 'link' && !choice.targetId) return true;
    return false;
  });

  const handleConfirm = () => {
    const decisions = rows.map((row) => {
      const choice = choices[adapter.rowKey(row)];
      return adapter.decision(row, choice.action, choice.action === 'link' ? choice.targetId : null);
    });
    onConfirm(decisions);
  };

  const statusText = (row) => {
    if (row.status === 'update') return t('admin.sync.statusUpdate');
    if (row.status === 'link') return t('admin.sync.statusLink');
    if (row.status === 'create') return t(`admin.sync.statusCreate.${provider}`);
    return t(`admin.sync.reason.${row.reason || 'unknown'}`);
  };

  const fieldLabel = (field) => t(`admin.sync.field.${field}`, { defaultValue: field });

  return (
    <Modal title={t(`admin.sync.title.${provider}`)} onClose={busy ? () => {} : onClose} size="lg">
      <Intro>{t(`admin.sync.intro.${provider}`)}</Intro>

      <Summary>
        {linkedCount > 0 && <Pill $tone="ok">{t('admin.sync.countLinked', { count: linkedCount })}</Pill>}
        {rows.filter((r) => r.status === 'update').length > 0 && (
          <Pill $tone="warn">{t('admin.sync.countUpdate', { count: rows.filter((r) => r.status === 'update').length })}</Pill>
        )}
        {rows.filter((r) => r.status === 'link').length > 0 && (
          <Pill $tone="ok">{t('admin.sync.countLink', { count: rows.filter((r) => r.status === 'link').length })}</Pill>
        )}
        {rows.filter((r) => r.status === 'create').length > 0 && (
          <Pill>{t('admin.sync.countCreate', { count: rows.filter((r) => r.status === 'create').length })}</Pill>
        )}
        {rows.filter((r) => r.status === 'decision').length > 0 && (
          <Pill $tone="bad">{t('admin.sync.countDecision', { count: rows.filter((r) => r.status === 'decision').length })}</Pill>
        )}
      </Summary>

      {!canCreate && rows.some((r) => r.status === 'create') && (
        <Warn>
          <FiAlertTriangle />
          {t(`admin.sync.createUnavailable.${provider}`, {
            missing: (preview.missingCreateConfig || []).join(', '),
          })}
        </Warn>
      )}

      {rows.map((row) => {
        const key = adapter.rowKey(row);
        const choice = choices[key] || { action: '', targetId: '' };
        const needsInput = row.status === 'decision';
        const targets = linkTargets(row);
        return (
          <RowCard key={key} $needs={needsInput}>
            <RowHead>
              <RecordName>{row.name || t('admin.sync.unnamed')}</RecordName>
              <RecordMeta>
                {[row.email, row.cpr ? `CPR ${row.cpr}` : null, row.cvr ? `CVR ${row.cvr}` : null].filter(Boolean).join(' · ')}
              </RecordMeta>
            </RowHead>

            <StatusLine $tone={row.status === 'decision' ? 'warn' : row.status === 'create' ? undefined : 'ok'}>
              {row.status === 'decision' ? <FiAlertTriangle /> : <FiArrowRight />}
              <span>
                {statusText(row)}
                {adapter.matchedExternal(row) && (row.status === 'update' || row.status === 'link') && (
                  <> — {adapter.externalLabel(adapter.matchedExternal(row))}</>
                )}
              </span>
            </StatusLine>

            {row.status === 'update' && row.updates && Object.keys(row.updates).length > 0 && (
              <UpdateList>
                {Object.entries(row.updates).map(([field, value]) => (
                  <li key={field}>
                    {fieldLabel(field)}: <code>{String(value)}</code>
                  </li>
                ))}
              </UpdateList>
            )}

            <Controls>
              <Select
                value={choice.action}
                disabled={busy}
                onChange={(e) => setChoice(key, { action: e.target.value })}
              >
                <option value="" disabled>{t('admin.sync.chooseAction')}</option>
                <option value="link">{t(`admin.sync.actionLink.${provider}`)}</option>
                {canCreate && <option value="create">{t(`admin.sync.actionCreate.${provider}`)}</option>}
                <option value="skip">{t('admin.sync.actionSkip')}</option>
              </Select>

              {choice.action === 'link' && (
                <Select
                  value={choice.targetId}
                  disabled={busy}
                  onChange={(e) => setChoice(key, { targetId: e.target.value })}
                >
                  <option value="" disabled>{t(`admin.sync.chooseTarget.${provider}`)}</option>
                  {targets.map((ext) => (
                    <option key={adapter.externalId(ext)} value={adapter.externalId(ext)}>
                      {adapter.externalLabel(ext)}
                    </option>
                  ))}
                </Select>
              )}
            </Controls>
          </RowCard>
        );
      })}

      {unmatchedExternal.length > 0 && (
        <>
          <SectionTitle>{t(`admin.sync.unmatchedExternal.${provider}`, { count: unmatchedExternal.length })}</SectionTitle>
          {unmatchedExternal.map((ext) => (
            <ExternalItem key={adapter.externalId(ext)}>{adapter.externalLabel(ext)}</ExternalItem>
          ))}
        </>
      )}

      {error && <ErrorLine style={{ marginTop: '1rem' }}><FiXCircle /> {error}</ErrorLine>}

      <Actions>
        <Btn onClick={handleConfirm} disabled={busy || incomplete}>
          {busy ? t('admin.sync.applying') : t('admin.sync.confirm')}
        </Btn>
        <Btn $variant="ghost" onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Btn>
        {incomplete && !busy && (
          <StatusLine $tone="warn" style={{ marginTop: 0 }}>
            <FiAlertTriangle /> {t('admin.sync.incompleteHint')}
          </StatusLine>
        )}
        {!incomplete && !busy && rows.length === 0 && (
          <StatusLine $tone="ok" style={{ marginTop: 0 }}>
            <FiCheckCircle /> {t('admin.sync.allLinked')}
          </StatusLine>
        )}
      </Actions>
    </Modal>
  );
};

export default SyncMatchDialog;
